export const WCAG_AA_TEXT_CONTRAST = 4.5;
export const WCAG_UI_CONTRAST = 3;
export const SEMANTIC_TYPOGRAPHY_MEDIUM_MIX = 0.10;
export const SEMANTIC_TYPOGRAPHY_LOW_MIX = 0.20;

export const parseColorToRgb = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw || raw.startsWith('var(')) {
    return null;
  }
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const normalized = hex[1].length === 3
      ? hex[1].split('').map((char) => `${char}${char}`).join('')
      : hex[1];
    return [
      Number.parseInt(normalized.slice(0, 2), 16),
      Number.parseInt(normalized.slice(2, 4), 16),
      Number.parseInt(normalized.slice(4, 6), 16),
    ];
  }
  const rgb = raw.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }
  const modernRgb = raw.match(/^rgba?\(\s*([\d.]+%?)\s+([\d.]+%?)\s+([\d.]+%?)/i);
  if (modernRgb) {
    return [modernRgb[1], modernRgb[2], modernRgb[3]].map((channel) => (
      String(channel).endsWith('%') ? Math.round((Number.parseFloat(channel) / 100) * 255) : Number.parseFloat(channel)
    ));
  }
  const srgb = raw.match(/^color\(\s*srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/i);
  if (srgb) {
    return [Number(srgb[1]), Number(srgb[2]), Number(srgb[3])].map((channel) => (
      channel <= 1 ? Math.round(channel * 255) : Math.round(channel)
    ));
  }
  return null;
};

const extractCssVariableFallback = (value = '') => {
  const raw = String(value || '').trim();
  const match = raw.match(/^var\(\s*(--[a-z0-9-_]+)\s*(?:,\s*(.+))?\)$/i);
  if (!match) {
    return '';
  }
  return String(match[2] || '').trim();
};

export const resolveCssColor = (value = '', scope = undefined, depth = 0) => {
  const raw = String(value || '').trim();
  if (!raw || depth > 4) {
    return raw;
  }
  if (!raw.startsWith('var(')) {
    return raw;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return extractCssVariableFallback(raw) || raw;
  }
  const variableName = raw.match(/^var\(\s*(--[a-z0-9-_]+)/i)?.[1] || '';
  const fallback = extractCssVariableFallback(raw);
  const computed = variableName
    ? window.getComputedStyle(scope || document.documentElement).getPropertyValue(variableName).trim()
    : '';
  return resolveCssColor(computed || fallback || raw, scope, depth + 1);
};

// Resolve variables embedded in composite CSS values (for example gradients).
// resolveCssColor intentionally handles a single color token; navigation and
// other state surfaces commonly use gradients made from semantic variables.
export const resolveCssVariablesInValue = (value = '', scope = undefined) => {
  const raw = String(value || '').trim();
  if (!raw.includes('var(')) {
    return raw;
  }
  return raw.replace(/var\(\s*(--[a-z0-9-_]+)\s*(?:,\s*([^)]+))?\)/gi, (match, variableName, fallback = '') => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return String(fallback || match).trim();
    }
    const computed = window.getComputedStyle(scope || document.documentElement)
      .getPropertyValue(variableName)
      .trim();
    return computed || String(fallback || match).trim();
  });
};

export const normalizeComparableColor = (value = '') => {
  const rgb = parseColorToRgb(resolveCssColor(value));
  if (!rgb) {
    return '';
  }
  return `#${rgb.map((channel) => Number(channel).toString(16).padStart(2, '0')).join('')}`;
};

export const relativeLuminance = (rgb = []) => {
  const [red = 255, green = 255, blue = 255] = rgb.map((channel) => {
    const value = Math.max(0, Math.min(255, channel)) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

export const contrastRatio = (foreground = '', background = '') => {
  const foregroundColor = parseColorToRgb(resolveCssColor(foreground));
  const backgroundColor = parseColorToRgb(resolveCssColor(background));
  if (!foregroundColor || !backgroundColor) {
    return 21;
  }
  const foregroundLuminance = relativeLuminance(foregroundColor);
  const backgroundLuminance = relativeLuminance(backgroundColor);
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

export const isReadablePair = (foreground = '', background = '', minimumRatio = WCAG_AA_TEXT_CONTRAST) => (
  contrastRatio(foreground, background) >= minimumRatio
);

export const isDarkColorValue = (value = '') => {
  const color = parseColorToRgb(resolveCssColor(value));
  return color ? relativeLuminance(color) < 0.28 : false;
};

export const isLightColor = (value = '') => {
  const color = parseColorToRgb(resolveCssColor(value));
  return color ? relativeLuminance(color) >= 0.5 : false;
};

export const extractBackgroundColors = (background = '', options = {}) => {
  const resolved = resolveCssVariablesInValue(resolveCssColor(background, options.scope), options.scope);
  const tokens = String(resolved || '').match(
    /#[0-9a-f]{3,8}\b|rgba?\([^)]+\)|color\(\s*srgb\s+[^)]+\)/gi,
  ) || [];
  const candidates = tokens.length ? tokens : [resolved];
  return candidates.filter((candidate) => parseColorToRgb(candidate));
};

export const getAccessibleForeground = (background = '', preferred = '', options = {}) => {
  const minimumRatio = Number(options.minimumRatio || WCAG_AA_TEXT_CONTRAST);
  const samples = extractBackgroundColors(background, options);
  if (!samples.length) {
    return preferred || options.fallback || options.dark || '#0f172a';
  }

  const candidates = [
    preferred,
    options.fallback,
    options.light || '#ffffff',
    options.dark || '#0f172a',
  ].filter((candidate, index, values) => (
    candidate
    && values.indexOf(candidate) === index
    && parseColorToRgb(resolveCssColor(candidate, options.scope))
  ));
  const scored = candidates.map((candidate) => ({
    candidate,
    ratio: Math.min(...samples.map((sample) => contrastRatio(candidate, sample))),
  }));
  const preferredScore = scored.find(({ candidate }) => candidate === preferred);
  if (preferredScore?.ratio >= minimumRatio) {
    return preferred;
  }
  const accessible = scored
    .filter(({ ratio }) => ratio >= minimumRatio)
    .sort((left, right) => right.ratio - left.ratio);
  if (accessible.length) {
    return accessible[0].candidate;
  }
  return scored.sort((left, right) => right.ratio - left.ratio)[0]?.candidate
    || options.dark
    || '#0f172a';
};

export const getReadableColor = (preferred = '', background = '', fallback = '', options = {}) => {
  const resolvedBackground = resolveCssColor(background, options.scope);
  if (!extractBackgroundColors(resolvedBackground, options).length) {
    return preferred || fallback;
  }
  return getAccessibleForeground(resolvedBackground, preferred, {
    ...options,
    fallback,
  });
};

export const resolveForegroundColor = (backgroundColor = '', options = {}) => {
  return getAccessibleForeground(backgroundColor, options.preferred || '', options);
};

export const resolveAdaptiveForeground = ({
  background = '',
  preferred = '',
  fallback = '',
  scope = undefined,
  minimumRatio = WCAG_AA_TEXT_CONTRAST,
} = {}) => {
  const resolvedBackground = resolveCssColor(background, scope);
  if (!parseColorToRgb(resolvedBackground)) {
    return preferred || fallback || 'var(--og-text-primary)';
  }
  return getReadableColor(preferred, resolvedBackground, fallback, { minimumRatio, scope });
};

export const resolveAdaptiveButtonText = (buttonBackground = '', preferred = '', options = {}) => (
  resolveAdaptiveForeground({
    background: buttonBackground || 'var(--og-button-primary)',
    preferred,
    fallback: options.fallback || 'var(--og-button-primary-text)',
    scope: options.scope,
    minimumRatio: options.minimumRatio || WCAG_AA_TEXT_CONTRAST,
  })
);

export const resolveLocalForeground = ({
  background = '',
  explicitForeground = '',
  themeForeground: legacyThemeForeground = '',
  semanticFallback = '',
  globalFallback = '',
  scope = undefined,
  minimumRatio = WCAG_AA_TEXT_CONTRAST,
} = {}) => {
  const resolvedBackground = resolveCssColor(background, scope);
  if (!parseColorToRgb(resolvedBackground)) {
    return explicitForeground || semanticFallback || globalFallback || legacyThemeForeground || 'var(--og-text-primary)';
  }
  return getReadableColor(
    explicitForeground,
    resolvedBackground,
    semanticFallback || globalFallback || 'var(--og-text-primary)',
    { minimumRatio, scope },
  );
};

export const resolveAdaptiveOverlayText = (background = '', preferred = '', options = {}) => (
  resolveAdaptiveForeground({
    background,
    preferred,
    fallback: options.fallback || 'var(--og-text-on-image)',
    scope: options.scope,
    minimumRatio: options.minimumRatio || WCAG_AA_TEXT_CONTRAST,
  })
);

export const colorToCssRgb = (value = '', scope = undefined) => {
  const color = parseColorToRgb(resolveCssColor(value, scope));
  return color ? `rgb(${color[0]}, ${color[1]}, ${color[2]})` : '';
};

const mixColor = (background = '', foreground = '', amount = 0) => {
  const base = parseColorToRgb(resolveCssColor(background));
  const overlay = parseColorToRgb(resolveCssColor(foreground));
  if (!base || !overlay) return background;
  const weight = Math.max(0, Math.min(1, Number(amount) || 0));
  return `rgb(${base.map((channel, index) => Math.round(channel + ((overlay[index] - channel) * weight))).join(', ')})`;
};

export const deriveTypographyForeground = (foreground = '', background = '', targetMix = SEMANTIC_TYPOGRAPHY_MEDIUM_MIX, minimumRatio = WCAG_AA_TEXT_CONTRAST) => {
  const resolvedForeground = colorToCssRgb(foreground) || resolveCssColor(foreground);
  const resolvedBackground = colorToCssRgb(background) || resolveCssColor(background);
  if (!parseColorToRgb(resolvedForeground) || !parseColorToRgb(resolvedBackground)) return resolvedForeground || foreground;
  for (let mix = Math.max(0, Math.min(0.46, targetMix)); mix >= 0; mix -= 0.02) {
    const candidate = mixColor(resolvedForeground, resolvedBackground, mix);
    if (contrastRatio(candidate, resolvedBackground) >= minimumRatio) return candidate;
  }
  return resolvedForeground;
};

/**
 * Complete reusable-component contract. State surfaces are derived only from
 * the final component background; theme/page mode never participates.
 */
export const buildComponentSurfaceVariables = (background = '', preferred = '', options = {}) => {
  const resolvedBackground = colorToCssRgb(background, options.scope) || resolveCssColor(background, options.scope);
  const foreground = resolveAdaptiveForeground({
    background: resolvedBackground,
    preferred,
    fallback: options.fallback || 'var(--og-text-primary)',
    scope: options.scope,
  });
  const hover = mixColor(resolvedBackground, foreground, 0.08);
  const selected = mixColor(resolvedBackground, foreground, 0.14);
  const pressed = mixColor(resolvedBackground, foreground, 0.2);
  const disabled = mixColor(resolvedBackground, foreground, 0.05);
  const stateForeground = (surface) => resolveForegroundColor(surface, { preferred: foreground });
  const mediumForeground = deriveTypographyForeground(foreground, resolvedBackground, SEMANTIC_TYPOGRAPHY_MEDIUM_MIX);
  const lowForeground = deriveTypographyForeground(foreground, resolvedBackground, SEMANTIC_TYPOGRAPHY_LOW_MIX);
  return {
    '--og-surface-background': resolvedBackground || 'transparent',
    '--og-surface-foreground': foreground,
    '--og-typography-high': foreground,
    '--og-typography-medium': mediumForeground,
    '--og-typography-low': lowForeground,
    // Named typography roles are part of every surface contract. Components
    // consume meaning, while this resolver derives the actual color from the
    // rendered surface and guarantees the requested contrast ratio.
    '--og-text-primary': foreground,
    '--og-text-secondary': mediumForeground,
    '--og-text-tertiary': lowForeground,
    '--og-text-muted': lowForeground,
    '--og-overline': mediumForeground,
    '--og-caption': lowForeground,
    '--og-label': foreground,
    '--og-helper': lowForeground,
    '--og-breadcrumb': mediumForeground,
    '--og-metadata': lowForeground,
    '--og-section-title': foreground,
    '--og-card-title': foreground,
    '--og-interactive-text': foreground,
    '--og-surface-border': foreground,
    '--og-surface-hover': hover,
    '--og-surface-hover-foreground': stateForeground(hover),
    '--og-surface-focus': foreground,
    '--og-surface-disabled': disabled,
    '--og-surface-disabled-foreground': stateForeground(disabled),
    '--og-surface-selected': selected,
    '--og-surface-selected-foreground': stateForeground(selected),
    '--og-surface-pressed': pressed,
    '--og-surface-pressed-foreground': stateForeground(pressed),
    '--og-surface-icon': foreground,
    '--og-surface-link': foreground,
    '--og-surface-badge': selected,
    '--og-surface-badge-foreground': stateForeground(selected),
  };
};

export const imageLuminanceToForeground = (luminance = 1) => (
  Number(luminance) < 0.42 ? '#ffffff' : '#0f172a'
);

export const imageLuminanceToOverlay = (luminance = 1) => {
  const value = Number(luminance);
  if (!Number.isFinite(value)) {
    return 'linear-gradient(180deg, rgba(15,23,42,0.16), rgba(15,23,42,0.42))';
  }
  if (value < 0.36) {
    return 'linear-gradient(180deg, rgba(15,23,42,0.08), rgba(15,23,42,0.38))';
  }
  if (value > 0.72) {
    return 'linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.42))';
  }
  return 'linear-gradient(180deg, rgba(15,23,42,0.20), rgba(15,23,42,0.52))';
};

export const buildAdaptiveSurfaceVariables = (background = '', preferred = '', options = {}) => {
  const foreground = resolveAdaptiveForeground({
    background,
    preferred,
    fallback: options.fallback || 'var(--og-text-primary)',
    scope: options.scope,
    minimumRatio: options.minimumRatio || WCAG_AA_TEXT_CONTRAST,
  });
  return {
    ...buildComponentSurfaceVariables(background, preferred, options),
    '--og-container-background': background || 'transparent',
    '--og-container-surface': background || 'transparent',
    '--og-container-foreground': foreground,
    '--og-container-secondary-foreground': deriveTypographyForeground(foreground, background, SEMANTIC_TYPOGRAPHY_MEDIUM_MIX),
    '--og-container-icon': foreground,
    '--og-container-border': colorToCssRgb(foreground, options.scope) || 'currentColor',
    '--og-container-link': foreground,
    '--og-container-link-visited': foreground,
    '--og-container-focus': foreground,
    '--og-local-background': background || 'transparent',
    '--og-local-surface': background || 'transparent',
    '--og-local-foreground': foreground,
    '--og-local-icon': foreground,
    '--og-text-on-local-surface': foreground,
    '--og-icon-on-local-surface': foreground,
    '--og-adaptive-foreground': foreground,
    '--og-inline-editable-foreground': foreground,
    '--og-local-border': colorToCssRgb(foreground, options.scope) || 'currentColor',
  };
};

export const buildAdaptiveButtonVariables = (background = '', preferred = '', options = {}) => {
  const foreground = resolveLocalForeground({
    background: background || 'var(--og-button-primary)',
    explicitForeground: preferred,
    semanticFallback: options.fallback || 'var(--og-button-primary-text)',
    globalFallback: 'var(--og-text-primary)',
    scope: options.scope,
    minimumRatio: options.minimumRatio || WCAG_AA_TEXT_CONTRAST,
  });
  return {
    ...buildComponentSurfaceVariables(background || 'var(--og-button-primary)', preferred, options),
    '--og-container-background': background || 'var(--og-button-primary)',
    '--og-container-surface': background || 'var(--og-button-primary)',
    '--og-container-foreground': foreground,
    '--og-container-secondary-foreground': foreground,
    '--og-container-icon': foreground,
    '--og-container-border': colorToCssRgb(foreground, options.scope) || 'currentColor',
    '--og-container-link': foreground,
    '--og-container-link-visited': foreground,
    '--og-container-focus': foreground,
    '--og-button-contained-bg': background || 'var(--og-button-primary)',
    '--og-button-contained-text': foreground,
    '--og-text-on-button': foreground,
    '--og-local-background': background || 'var(--og-button-primary)',
    '--og-local-surface': background || 'var(--og-button-primary)',
    '--og-local-foreground': foreground,
    '--og-local-icon': foreground,
    '--og-text-on-local-surface': foreground,
    '--og-icon-on-local-surface': foreground,
    '--og-adaptive-foreground': foreground,
    '--og-inline-editable-foreground': foreground,
  };
};

export const parseColor = parseColorToRgb;
export const normalizeColor = normalizeComparableColor;
export const getRelativeLuminance = relativeLuminance;
export const getContrastRatio = contrastRatio;
export const chooseReadableForeground = getReadableColor;
export const getContrastText = getAccessibleForeground;

export const buildAccessiblePair = (foreground = '', background = '', fallbackForeground = '', options = {}) => ({
  background,
  requestedForeground: foreground,
  foreground: getReadableColor(foreground, background, fallbackForeground, options),
  ratio: contrastRatio(getReadableColor(foreground, background, fallbackForeground, options), background),
});

export const buildSemanticForegrounds = (theme = {}) => {
  const dark = Boolean(theme.dark);
  const darkFallback = dark ? '#f8fafc' : '#0f172a';
  const mutedFallback = dark ? '#cbd5e1' : '#475569';
  const appBackground = theme.appBackground || theme.pageBackground;
  const surfaceBackground = theme.surface || theme.softSurface || appBackground;
  const cardBackground = theme.elevatedSurface || surfaceBackground;
  const inputBackground = theme.inputSurface || surfaceBackground;
  const buttonBackground = theme.accentBackground || '#0a4da2';
  const controlTrack = theme.switchTrack || (dark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(15, 23, 42, 0.18)');
  const controlActiveTrack = theme.switchTrackChecked || buttonBackground;

  return {
    textPrimary: getReadableColor(theme.textColor, appBackground, darkFallback),
    textSecondary: getReadableColor(theme.mutedTextColor, appBackground, mutedFallback),
    textOnSurface: getReadableColor(theme.textColor, surfaceBackground, darkFallback),
    textOnCard: getReadableColor(theme.textColor, cardBackground, darkFallback),
    textOnInput: getReadableColor(theme.textColor, inputBackground, darkFallback),
    textOnButton: getReadableColor(theme.accentTextColor, buttonBackground, '#ffffff'),
    textAdaptive: getReadableColor(theme.textColor, surfaceBackground, darkFallback),
    textOnDark: '#ffffff',
    textOnLight: '#0f172a',
    textOnDarkSurface: '#ffffff',
    textOnLightSurface: '#0f172a',
    textOnImage: '#ffffff',
    textOnOverlay: '#ffffff',
    buttonTextAdaptive: getReadableColor(theme.accentTextColor, buttonBackground, '#ffffff'),
    iconPrimary: getReadableColor(theme.iconPrimary || theme.textColor, appBackground, darkFallback),
    iconOnSurface: getReadableColor(theme.iconPrimary || theme.textColor, surfaceBackground, darkFallback),
    iconOnCard: getReadableColor(theme.iconPrimary || theme.textColor, cardBackground, darkFallback),
    iconOnButton: getReadableColor(theme.accentTextColor, buttonBackground, '#ffffff'),
    controlForeground: getReadableColor(theme.textColor, theme.inputSurface || surfaceBackground, darkFallback),
    controlKnob: getReadableColor(theme.switchThumb, controlTrack, dark ? '#f8fafc' : '#0f172a', { minimumRatio: WCAG_UI_CONTRAST }),
    controlKnobActive: getReadableColor(theme.switchThumbChecked || theme.accentTextColor, controlActiveTrack, '#ffffff', { minimumRatio: WCAG_UI_CONTRAST }),
    controlCheckmark: getReadableColor(theme.switchThumbChecked || theme.accentTextColor, controlActiveTrack, '#ffffff', { minimumRatio: WCAG_UI_CONTRAST }),
  };
};

export const defaultThemeContrastConstraints = [
  { textKey: 'globalTextColor', backgroundKeys: ['screenBackground', 'pageBackground', 'cardBackground'] },
  { textKey: 'titleColor', backgroundKeys: ['pageBackground', 'cardBackground', 'screenBackground'] },
  { textKey: 'bodyColor', backgroundKeys: ['pageBackground', 'cardBackground', 'screenBackground'] },
  { textKey: 'textColor', backgroundKeys: ['pageBackground', 'cardBackground', 'screenBackground'] },
  { textKey: 'buttonTextColor', backgroundKeys: ['buttonBackground'] },
  { textKey: 'iconColor', backgroundKeys: ['pageBackground', 'cardBackground', 'buttonBackground'] },
  { textKey: 'fieldTextColor', backgroundKeys: ['inputBackground', 'fieldBackground', 'cardBackground'] },
];

export const findThemeColorConflict = (theme = {}, changedKey = '', changedValue = undefined, options = {}) => {
  const constraints = options.constraints || defaultThemeContrastConstraints;
  const minimumRatio = Number(options.minimumRatio || WCAG_AA_TEXT_CONTRAST);
  const nextTheme = {
    ...(theme || {}),
    ...(changedKey ? { [changedKey]: changedValue } : {}),
  };
  for (const constraint of constraints) {
    const textColor = normalizeComparableColor(nextTheme[constraint.textKey]);
    if (!textColor) {
      continue;
    }
    for (const backgroundKey of constraint.backgroundKeys) {
      const backgroundColor = normalizeComparableColor(nextTheme[backgroundKey]);
      if (backgroundColor && !isReadablePair(textColor, backgroundColor, minimumRatio)) {
        return {
          textKey: constraint.textKey,
          backgroundKey,
          color: textColor,
          ratio: contrastRatio(textColor, backgroundColor),
          minimumRatio,
        };
      }
    }
  }
  return null;
};

export const ContrastAwareThemeEngine = {
  WCAG_AA_TEXT_CONTRAST,
  WCAG_UI_CONTRAST,
  parseColorToRgb,
  normalizeComparableColor,
  resolveCssColor,
  relativeLuminance,
  contrastRatio,
  isReadablePair,
  isDarkColorValue,
  isLightColor,
  extractBackgroundColors,
  getAccessibleForeground,
  getContrastText,
  getReadableColor,
  parseColor,
  normalizeColor,
  getRelativeLuminance,
  getContrastRatio,
  chooseReadableForeground,
  resolveForegroundColor,
  resolveAdaptiveForeground,
  resolveAdaptiveButtonText,
  resolveLocalForeground,
  resolveAdaptiveOverlayText,
  colorToCssRgb,
  imageLuminanceToForeground,
  imageLuminanceToOverlay,
  buildAdaptiveSurfaceVariables,
  buildAdaptiveButtonVariables,
  buildAccessiblePair,
  buildSemanticForegrounds,
  findThemeColorConflict,
};
